import React, { useEffect, useRef } from 'react';
import { string } from 'prop-types';

// Termly-hosted policy embed.
//
// Termly renders the referenced policy document into an <iframe> inside the
// <div name="termly-embed" data-id="..."> once its loader script has run.
//
// WHY THIS COMPONENT OWNS THE SCRIPT (instead of injecting via react-helmet):
// The loader (embed-policy.min.js) is an IIFE that, at script-execution time,
// runs `document.getElementsByName("termly-embed").forEach(render)` — a single,
// synchronous DOM scan. Its only ongoing hook is a MutationObserver configured
// with `{ attributeFilter: ["data-id"], subtree: true }`; it re-renders only
// when a `data-id` ATTRIBUTE changes on an already-present element, NOT when a
// new embed node is added (there is no `childList` observation). It exposes no
// public re-init API (no `window.Termly.initEmbeds()`; the only globals it sets
// are iframe-resizer helpers). That creates two failure modes in a React SPA:
//
//   1. Initial load: an async Helmet-injected script can execute before React
//      mounts the div, so the one-shot scan finds nothing → blank iframe.
//   2. Client-side nav (e.g. /privacy-policy → /terms-of-service): the loader
//      already executed once and never re-scans, and the newly mounted div is a
//      childList addition the observer ignores → blank iframe.
//
// To make mounting deterministic we (a) inject the loader from a useEffect that
// runs only AFTER the div is committed to the DOM, and (b) on a subsequent mount
// where the loader tag already exists, remove and re-add it to force the IIFE to
// run its scan again against the current DOM.
const TERMLY_EMBED_SCRIPT_URL = 'https://app.termly.io/embed-policy.min.js';

/**
 * Render a Termly-hosted policy document (privacy policy, terms of service, etc.).
 *
 * @component
 * @param {Object} props
 * @param {string} props.dataId - The Termly document id (data-id attribute).
 * @returns {JSX.Element} the embed target div; the loader script is injected imperatively.
 */
const TermlyEmbed = props => {
  const { dataId } = props;
  const embedRef = useRef(null);

  useEffect(() => {
    // Guard: this effect runs post-commit, so the embed div is in the DOM. The
    // ref makes that dependency explicit — the loader's scan needs the div to
    // already exist.
    if (!embedRef.current) {
      return undefined;
    }

    // If a loader tag is already present (client-side nav from the other policy
    // page), the one-shot scan has already run and will not pick up this newly
    // mounted div. The loader has no re-init hook, so remove the stale tag and
    // re-inject a fresh one below to force the scan to run again.
    const existing = document.querySelector(`script[src="${TERMLY_EMBED_SCRIPT_URL}"]`);
    if (existing) {
      existing.remove();
    }

    const script = document.createElement('script');
    script.src = TERMLY_EMBED_SCRIPT_URL;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Remove the tag we added so the next mount always re-injects a fresh,
      // re-scanning copy rather than relying on an already-executed loader.
      script.remove();
    };
  }, [dataId]);

  return <div ref={embedRef} name="termly-embed" data-id={dataId}></div>;
};

TermlyEmbed.propTypes = {
  dataId: string.isRequired,
};

export default TermlyEmbed;
